// Claude: Discover.tsx — 2-tab orchestrator (Publish / My Requirements) 31-03-2026
// Claude: Retired: Browse Tags, Keyword Search, AI RFX tabs; SSE + localStorage caching removed 31-03-2026
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Image,
  VStack,
  Button,
  Badge,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
} from "@chakra-ui/react";
import { Select, ConfigProvider } from "antd";
import { MdTune, MdLabel, MdSearch } from "react-icons/md";
import { Images } from "../../Assets/images";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../redux/Actions";
import { Buyerscreens, RelevanceFieldsNewArr } from "../../Constants/data";
import { RfxAPI } from "../../Apis/rfxAPI";
import { AdminAPI } from "../../Apis/adminAPI";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { SellerAPI } from "../../Apis/sellerAPI";
import { DepartmentsAPI } from "../../Apis/departmentsAPI"; // Claude: department options for RFXPublish 08-May-2026
import { filterCountries } from "../../Utils/countries";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import RFXPublish from "./RFXPublish";
import MyRFXs from "./MyRFXs";
import RFXResponses from "./RFXResponses";
import Jumbotron from "../../Components/Common/Jumbotron";

// Claude: static country options for Select dropdown 06-03-2026
const COUNTRY_OPTIONS = filterCountries("").map((c) => ({ label: c.value, value: c.value }));

const TAB_PUBLISH = 0;
const TAB_RESULTS = 1;

const TAB_COLORS = ["#FFD4B2", "#C9BFF5"];
const TAB_BG = ["#FFF5EE", "#F3F0FF"];

// Claude: SearchFiltersPanel — filter drawer content 14-03-2026
interface SearchFiltersPanelProps {
  filters: { targetCountry: string[]; targetIndustries: string[]; targetRelevanceStatus: string[] };
  onFiltersChange: (f: { targetCountry: string[]; targetIndustries: string[]; targetRelevanceStatus: string[] }) => void;
  industryOptions: { label: string; value: string }[];
  reachCount: number | null;
  reachLoading: boolean;
  onSave: () => void;
}

const SearchFiltersPanel = ({
  filters, onFiltersChange,
  industryOptions,
  reachCount, reachLoading,
  onSave,
}: SearchFiltersPanelProps) => (
  <VStack spacing={3} alignItems="stretch">
    <Box>
      <Text fontSize="11px" color="gray.500" mb={1}>Sellers matching ANY of Country</Text>
      <Select mode="multiple" showSearch style={{ width: "100%" }}
        placeholder="Search and select countries" options={COUNTRY_OPTIONS}
        filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        value={filters.targetCountry}
        onChange={(v: string[]) => onFiltersChange({ ...filters, targetCountry: v })} />
    </Box>
    <Box>
      <Text fontSize="11px" color="gray.500" mb={1}><Text as="span" color="gray.400">(and) </Text>Sellers matching ANY of Industry</Text>
      <Select mode="multiple" showSearch style={{ width: "100%" }}
        placeholder="Select industries" options={industryOptions}
        filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        value={filters.targetIndustries}
        onChange={(v: string[]) => onFiltersChange({ ...filters, targetIndustries: v })} />
    </Box>
    <Box>
      <Text fontSize="11px" color="gray.500" mb={1}><Text as="span" color="gray.400">(and) </Text>Sellers with ANY of Relevance Status</Text>
      <Select mode="multiple" style={{ width: "100%" }} placeholder="Select relevance statuses"
        value={filters.targetRelevanceStatus}
        onChange={(v: string[]) => onFiltersChange({ ...filters, targetRelevanceStatus: v })}
        options={RelevanceFieldsNewArr.filter((r) => r.status !== "Not_Relevant").map((r) => ({
          label: (
            <Flex alignItems="center" gap="6px">
              <Box w="12px" h="12px" borderRadius="2px" bg={r.colorHash} flexShrink={0} />
              <Text fontSize="12px">{r.title}</Text>
            </Flex>
          ),
          value: r.status,
        }))} />
    </Box>
    <Flex justifyContent="space-between" alignItems="center">
      <Text fontSize="12px" color="gray.500">
        {reachLoading ? "Calculating reach…"
          : reachCount !== null && reachCount !== -1
            ? `Potentially reaching up to ${reachCount} seller${reachCount !== 1 ? "s" : ""}`
            : reachCount === -1 ? "Could not estimate reach" : "—"}
      </Text>
      <Button size="sm" variant="outline" borderColor="#1565C0" color="#1565C0"
        borderRadius="8px" fontSize="12px" px={3} h="28px"
        _hover={{ bg: "#E3F2FD" }}
        onClick={onSave}>
        Save Filters
      </Button>
    </Flex>
  </VStack>
);

const Discover = () => {
  const dispatch = useDispatch();
  const { success } = useToastWrapper();
  const { user: authUser, notification: { unreadNotifications } } = useSelector((state: any) => state);
  // Claude: profile for Jumbotron — fetched on mount 02-Apr-2026
  const [profile, setProfile] = useState<any>(null);
  useEffect(() => {
    BuyerAPI.getBuyerProfile().then((res: any) => setProfile(res?.data?.data)).catch(() => {});
  }, []);

  // Claude: selected tags — persisted to localStorage per user 31-03-2026
  const tagsKey = authUser?.id ? `aftr_buyer_discover_tags_${authUser.id}` : null;
  const [selectedTags, setSelectedTags] = useState<string[]>(() => {
    if (!tagsKey) return [];
    try { return JSON.parse(localStorage.getItem(tagsKey) ?? "[]") ?? []; }
    catch { return []; }
  });

  // Claude: shared filter state (country / industry / relevance) 14-03-2026
  const [drawerFilters, setDrawerFilters] = useState({
    targetCountry: [] as string[],
    targetIndustries: [] as string[],
    targetRelevanceStatus: [] as string[],
  });
  const [industryOptions, setIndustryOptions] = useState<{ label: string; value: string }[]>([]);
  const industryNodes = useRef<{ _id: string; name: string }[]>([]);
  const profilePrefilled = useRef(false);

  const [createReachCount, setCreateReachCount] = useState<number | null>(null);
  const [createReachLoading, setCreateReachLoading] = useState(false);

  // Tags drawer state
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagSearch, setTagSearch] = useState("");

  // Claude: departments lifted from RFXPublish — needed to include in reach count 08-May-2026
  const [departments, setDepartments] = useState<string[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]);

  const [activeTab, setActiveTab] = useState(TAB_PUBLISH);
  const [editingRfx, setEditingRfx] = useState<any | null>(null);
  // Claude: lifted from MyRFXs — render RFXResponses as full-page replacement outside tab structure 01-Apr-2026
  const [selectedRfx, setSelectedRfx] = useState<any | null>(null);
  const { isOpen: isFilterDrawerOpen, onOpen: onFilterDrawerOpen, onClose: onFilterDrawerClose } = useDisclosure();
  const { isOpen: isTagsDrawerOpen, onOpen: onTagsDrawerOpen, onClose: onTagsDrawerClose } = useDisclosure();

  // Claude: pre-fill filters + tags from RFX being edited; switch to Publish tab 31-03-2026
  const handleEdit = (rfx: any) => {
    setEditingRfx(rfx);
    setDrawerFilters({
      targetCountry: rfx.targetCountry ?? [],
      targetIndustries: rfx.targetIndustries ?? [],
      targetRelevanceStatus: rfx.targetRelevanceStatus ?? [],
    });
    setSelectedTags(rfx.targetTags ?? []);
    setActiveTab(TAB_PUBLISH);
  };

  // Claude: persist tags selection to localStorage on change 31-03-2026
  useEffect(() => {
    if (!tagsKey) return;
    localStorage.setItem(tagsKey, JSON.stringify(selectedTags));
  }, [selectedTags, tagsKey]);

  // Claude: refresh reach count when departments or tags change 08-May-2026
  useEffect(() => {
    if (!profilePrefilled.current) return;
    fetchCreateReachCount({ ...drawerFilters, targetTags: selectedTags, departments });
  }, [departments]);

  useEffect(() => {
    if (!profilePrefilled.current) return;
    fetchCreateReachCount({ ...drawerFilters, targetTags: selectedTags, departments });
  }, [selectedTags]);

  const filtersActive =
    drawerFilters.targetCountry.length > 0 ||
    drawerFilters.targetIndustries.length > 0 ||
    drawerFilters.targetRelevanceStatus.length > 0;

  // Claude: fetch department options on mount 08-May-2026
  useEffect(() => {
    DepartmentsAPI.getAll()
      .then((res: any) => {
        const list: any[] = Array.isArray(res) ? res : (res?.data ?? []);
        setDepartmentOptions(list.map((d: any) => ({ label: d.name, value: String(d._id) })));
      })
      .catch(() => {});
  }, []);

  const fetchReachCount = useCallback(async (
    filters: { targetCountry: string[]; targetIndustries: string[]; targetRelevanceStatus: string[]; targetTags?: string[]; departments?: string[] },
    setCount: (n: number | null) => void,
    setLoading: (b: boolean) => void,
  ) => {
    setLoading(true);
    try {
      const res = await RfxAPI.getRfxReachCount(filters);
      setCount(typeof res?.data === "number" ? res.data : null);
    } catch { setCount(-1); }
    finally { setLoading(false); }
  }, []);

  const fetchCreateReachCount = useCallback((filters: any) =>
    fetchReachCount(filters, setCreateReachCount, setCreateReachLoading), [fetchReachCount]);

  // Claude: fetch buyer profile defaults for filter pre-fill 06-03-2026
  const fetchProfileDefaults = useCallback(async () => {
    try {
      const res = await BuyerAPI.getBuyerProfile();
      const profile = res?.data?.data ?? res?.data ?? res;
      const knownIds = new Set(industryNodes.current.map((n) => n._id));
      const resolvedIndustries = (profile.industryIds ?? [])
        .map((id: any) => String(id))
        .filter((id: string) => knownIds.has(id));
      return {
        targetCountry: profile.buyerPersonCountry ? [profile.buyerPersonCountry] : [],
        targetIndustries: resolvedIndustries,
        targetRelevanceStatus: [] as string[],
      };
    } catch {
      return { targetCountry: [], targetIndustries: [], targetRelevanceStatus: [] };
    }
  }, []);

  // Claude: fetch industry list then pre-fill filters from buyer profile 06-03-2026
  useEffect(() => {
    AdminAPI.getIndustryTree().then((res: any) => {
      const nodes: any[] = Array.isArray(res?.data) ? res.data : (res?.data?.data ?? []);
      industryNodes.current = nodes.map((n: any) => ({ _id: String(n._id), name: n.name }));
      setIndustryOptions(nodes.map((n: any) => ({ label: n.name, value: String(n._id) })));
      fetchProfileDefaults().then((defaults) => {
        const saved = localStorage.getItem("aftr_buyer_search_filters");
        if (saved) {
          try {
            const p = JSON.parse(saved);
            const f = {
              targetCountry: p.targetCountry ?? [],
              targetIndustries: p.targetIndustries ?? [],
              targetRelevanceStatus: p.targetRelevanceStatus ?? [],
            };
            setDrawerFilters(f);
            fetchCreateReachCount({ ...f, targetTags: selectedTags, departments });
          } catch {
            setDrawerFilters(defaults);
            fetchCreateReachCount({ ...defaults, targetTags: selectedTags, departments });
          }
        } else {
          setDrawerFilters(defaults);
          fetchCreateReachCount({ ...defaults, targetTags: selectedTags, departments });
        }
        profilePrefilled.current = true;
      });
    }).catch(() => {});
  }, []);

  // Claude: load tags when tags drawer opens (lazy) 31-03-2026
  const handleOpenTagsDrawer = () => {
    onTagsDrawerOpen();
    if (allTags.length === 0) {
      setTagsLoading(true);
      SellerAPI.getSellerTags()
        .then((res: any) => {
          const tags: string[] = Array.isArray(res?.data) ? res.data : [];
          setAllTags(tags.sort((a, b) => a.localeCompare(b)));
        })
        .catch(() => {})
        .finally(() => setTagsLoading(false));
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  useEffect(() => {
    dispatch(actions.buyerMenu.set({
      activeMenu: Buyerscreens.RFX,
      aiChatContext: "create-rfx",
    }));
  }, []);

  const accentColor = TAB_COLORS[activeTab] ?? TAB_COLORS[0];

  // Claude: RFXResponses view — dark header with RFX ID + AFTR logo 10-04-2026
  if (selectedRfx) {
    return (
      <Box w="100%">
        <Flex w="100%" px="30px" py={5} bg="#0E122F" color="white"
          alignItems="center" justifyContent="space-between">
          <Text fontSize="xl" fontWeight={700}>{selectedRfx.rfxId ?? "Requirement"}</Text>
          <Flex flexDirection="column" alignItems="center">
            <Image src={Images.AftrLogo} h="32px" objectFit="contain" />
            <Text fontSize="11px" fontWeight={300} color="whiteAlpha.600" letterSpacing="wide">buyer (beta)</Text>
          </Flex>
        </Flex>
        <RFXResponses rfx={selectedRfx} onBack={() => setSelectedRfx(null)} buyerPhoneNumber={profile?.buyerPhoneNumber} />
      </Box>
    );
  }

  return (
    <Box w="100%">
      {/* Claude: Jumbotron replaces plain dark header — Discover is new home 02-Apr-2026 */}
      <Jumbotron
        personName={profile?.buyerPersonName ?? ""}
        companyName={profile?.buyerCompanyName ?? ""}
        unreadNotifications={unreadNotifications}
        role="Buyer"
        navigateUrl="/buyer/notifications"
        profileNavigateUrl="/buyer/myaccount"
        linkedinPhoto={profile?.linkedinPhoto}
        verificationStatus={authUser?.verificationStatus}
      />

      {/* Claude: no horizontal padding on outer box — tab panels fill full width naturally 01-Apr-2026 */}
      <Box w="100%" bg="white" pb="30px">
        <Box px="20px" pt={4}>
          <Text fontSize="12px" color="gray.400" mb={3}>
            Find solutions from solution partners for your requirements.
          </Text>
        </Box>
        <Tabs variant="unstyled" isLazy index={activeTab} onChange={setActiveTab}>
          <Flex alignItems="flex-end" borderBottom="2px solid" borderColor={accentColor}>
            <TabList gap={0} flex={1} border="none" w="100%">
                {/* Tab 0: Publish */}
                <Tab fontSize="sm" fontWeight={600} px={3} py={2} borderRadius="8px 8px 0 0" flex={1}
                  border="1px solid"
                  borderBottom={activeTab === TAB_PUBLISH ? "none" : `2px solid ${accentColor}`}
                  mb={activeTab === TAB_PUBLISH ? "-2px" : "0"}
                  bg={activeTab === TAB_PUBLISH ? "#0E122F" : "gray.100"}
                  color={activeTab === TAB_PUBLISH ? "white" : "gray.400"}
                  borderColor={activeTab === TAB_PUBLISH ? "#0E122F" : "gray.200"}>
                  {editingRfx ? "Edit" : "Publish Requirement"}
                </Tab>
                {/* Tab 1: My Requirements */}
                <Tab fontSize="sm" fontWeight={600} px={3} py={2} borderRadius="8px 8px 0 0" flex={1}
                  border="1px solid"
                  borderBottom={activeTab === TAB_RESULTS ? "none" : `2px solid ${accentColor}`}
                  mb={activeTab === TAB_RESULTS ? "-2px" : "0"}
                  bg={activeTab === TAB_RESULTS ? "#0E122F" : "gray.100"}
                  color={activeTab === TAB_RESULTS ? "white" : "gray.400"}
                  borderColor={activeTab === TAB_RESULTS ? "#0E122F" : "gray.200"}>
                  Responses
                </Tab>
              </TabList>
          </Flex>

          <TabPanels>
            {/* Tab 0: Publish — full width beige bg */}
            <TabPanel px="20px" py={4} bg={TAB_BG[TAB_PUBLISH]}>
              <RFXPublish
                filters={drawerFilters}
                selectedTags={selectedTags}
                filtersActive={filtersActive}
                onOpenFilterDrawer={onFilterDrawerOpen}
                onOpenTagsDrawer={handleOpenTagsDrawer}
                onPublished={(planExpired?: boolean) => { // Claude: planExpired — update profile state so locked form shows 20-04-2026
                  if (planExpired) setProfile((p: any) => ({ ...p, isPlanActive: false }));
                  setActiveTab(TAB_RESULTS);
                }}
                editingRfx={editingRfx}
                onEditDone={() => setEditingRfx(null)}
                onCancelEdit={() => { setEditingRfx(null); setActiveTab(TAB_RESULTS); }}
                isPlanActive={profile?.isPlanActive}
                departments={departments}
                onDepartmentsChange={setDepartments}
                departmentOptions={departmentOptions}
                reachCount={createReachCount}
                reachLoading={createReachLoading}
              />
            </TabPanel>

            {/* Tab 1: My Requirements — full width purple bg; minH fills viewport so beige layout bg doesn't bleed through 02-Apr-2026 */}
            <TabPanel px="20px" py={4} bg={TAB_BG[TAB_RESULTS]} minH="100dvh">
              <MyRFXs industryOptions={industryOptions} onEdit={handleEdit} onSelectRfx={setSelectedRfx} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Shared filter drawer */}
      <Drawer isOpen={isFilterDrawerOpen} placement="right" onClose={onFilterDrawerClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md" fontWeight={700} borderBottomWidth="1px">
            <Flex alignItems="center" gap={2}>
              Search Filters
              {filtersActive && <Text fontSize="xs" color="#1565C0" fontWeight={600}>Filters Active</Text>}
            </Flex>
          </DrawerHeader>
          {/* Claude: ConfigProvider raises Ant Design popup z-index above Chakra Drawer (~1400) 14-03-2026 */}
          <DrawerBody py={4}>
            <ConfigProvider theme={{ token: { zIndexPopupBase: 1500 } }}>
              <SearchFiltersPanel
                filters={drawerFilters}
                onFiltersChange={(f) => {
                  setDrawerFilters(f);
                  fetchCreateReachCount({ ...f, targetTags: selectedTags, departments });
                }}
                industryOptions={industryOptions}
                reachCount={createReachCount}
                reachLoading={createReachLoading}
                onSave={() => {
                  localStorage.setItem("aftr_buyer_search_filters", JSON.stringify(drawerFilters));
                  success("Filters saved");
                  onFilterDrawerClose();
                }}
              />
            </ConfigProvider>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Tags drawer */}
      <Drawer isOpen={isTagsDrawerOpen} placement="right" onClose={onTagsDrawerClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md" fontWeight={700} borderBottomWidth="1px">
            <Flex alignItems="center" gap={2}>
              <MdLabel size={18} color="#FF8A65" />
              Select Matching Tags
              {selectedTags.length > 0 && (
                <Badge colorScheme="orange" borderRadius="full" fontSize="10px" textTransform="none">
                  {selectedTags.length} selected
                </Badge>
              )}
            </Flex>
          </DrawerHeader>
          <DrawerBody py={3}>
            <InputGroup size="sm" mb={3}>
              <InputLeftElement pointerEvents="none">
                <MdSearch color="#A0AEC0" />
              </InputLeftElement>
              <Input
                placeholder="Search tags..."
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                borderRadius="8px" fontSize="12px" focusBorderColor="#FF8A65" bg="white"
              />
            </InputGroup>

            {selectedTags.length > 0 && (
              <Flex justifyContent="flex-end" mb={2}>
                <Text fontSize="11px" color="gray.400" cursor="pointer"
                  _hover={{ color: "#C62828" }}
                  onClick={() => setSelectedTags([])}>
                  Clear selection ({selectedTags.length})
                </Text>
              </Flex>
            )}

            {tagsLoading ? (
              <Flex justifyContent="center" py={8}><Spinner size="sm" color="#0E122F" /></Flex>
            ) : allTags.length === 0 ? (
              <Text fontSize="xs" color="gray.400">No tags available yet.</Text>
            ) : (
              <Flex flexWrap="wrap" gap={2}>
                {allTags
                  .filter((t) => t.toLowerCase().includes(tagSearch.toLowerCase()))
                  .map((tag) => {
                    const isSelected = selectedTags.includes(tag);
                    return (
                      <Badge
                        key={tag} px={3} py={1} borderRadius="full"
                        bg={isSelected ? "#FF8A65" : "gray.100"}
                        color={isSelected ? "white" : "gray.600"}
                        border="1px solid"
                        borderColor={isSelected ? "#FF8A65" : "gray.200"}
                        cursor="pointer" fontSize="11px" textTransform="none"
                        fontWeight={isSelected ? 600 : 400}
                        _hover={{ bg: isSelected ? "#F4511E" : "#FFE0B2", borderColor: isSelected ? "#F4511E" : "#FFCC80" }}
                        onClick={() => toggleTag(tag)}
                      >
                        {tag}
                      </Badge>
                    );
                  })}
              </Flex>
            )}

            <Box mt={4}>
              <Button w="100%" size="sm" bg="#0E122F" color="white" borderRadius="8px"
                _hover={{ bg: "#159300" }} onClick={onTagsDrawerClose}>
                Apply
              </Button>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Discover;
